<template>
  <div id="ztws-events-create">
    <v-overlay :value="ProgressLoadingFlag">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>

    <!-- Premium Page Header -->
    <v-card elevation="20" class="header-card mb-6 overflow-hidden">
      <div class="header-gradient">
        <div class="header-backdrop-effect"></div>
        <div class="header-content pa-8">
          <v-row align="center" justify="space-between">
            <!-- Left Side: Title and Icon -->
            <v-col cols="12" md="8" class="d-flex align-center">
              <div class="header-icon-box">
                <v-icon size="40px" color="white" class="mdi"
                  >mdi-microphone</v-icon
                >
              </div>
              <div class="ml-4">
                <h1 class="header-title white--text mb-0">{{ PageTitle }}</h1>
                <p class="header-subtitle white--text mb-0">
                  {{ PageDescription }}
                </p>
              </div>
            </v-col>

            <!-- Right Side: Action Buttons -->
            <v-col cols="12" md="4" class="text-right">
              <v-btn icon class="action-btn" @click="backPage">
                <v-icon>mdi-arrow-left</v-icon>
              </v-btn>
              <v-btn
                icon
                class="action-btn"
                @click="refreshPageData"
                :loading="LoadingFlag"
              >
                <v-icon>mdi-refresh</v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </div>
      </div>
    </v-card>

    <v-card-text v-if="PagePermissionFlag">
      <!-- Important Notes Alert -->
      <v-container class="notes alert-content" v-if="NotesFlag">
        <v-alert type="info" prominent border="left" class="notes-alert">
          <h4 class="alert-title">
            <v-icon small class="mr-2">mdi-information-outline</v-icon>Important
            Notes
          </h4>
          <ul class="alert-list">
            <li>Regular subscription can be paid only once.</li>
            <li>
              After paying regular subscription, choose additional subscription.
            </li>
            <li>
              Late fee is optional but paying late fee is required to get voting
              rights.
            </li>
            <li>
              After completing the payment, kindly select your members for whom
              the payment was made.
            </li>
          </ul>
        </v-alert>
      </v-container>

      <!-- Eligibility Check Alert - Premium Error -->
      <v-alert
        v-if="ShowEligibilityAlert && ContinuousYearsCount > 0"
        color="#dc2626"
        prominent
        border="left"
        class="premium-alert premium-alert--error mb-6"
      >
        <div class="premium-alert-content">
          <div class="alert-icon-wrapper">
            <v-icon size="28" class="alert-icon">mdi-alert-octagon</v-icon>
          </div>
          <div class="alert-body">
            <h4 class="alert-title premium-alert-title">
              <span>Continuous Years</span>
            </h4>
            <p class="premium-alert-message mb-0">{{ EligibilityMessage }}</p>
          </div>
        </div>
      </v-alert>

      <!-- Eligibility Success Alert - Premium Success -->
      <v-alert
        v-if="EligibilityFlag && EligibilityMessage && ContinuousYearsCount > 0"
        prominent
        border="left"
        class="premium-alert premium-alert--success mb-6"
      >
        <div class="premium-alert-content">
          <div class="alert-icon-wrapper">
            <v-icon size="28" class="alert-icon"
              >mdi-check-circle-outline</v-icon
            >
          </div>
          <div class="alert-body">
            <h4 class="alert-title premium-alert-title">
              <span>Continuous Years</span>
            </h4>
            <p class="premium-alert-message mb-0">{{ EligibilityMessage }}</p>
          </div>
        </div>
      </v-alert>

      <!-- Profile Check Alert - Premium Error -->
      <v-alert
        v-if="ShowProfileAlert && IsProfileRequired > 0"
        color="#dc2626"
        prominent
        border="left"
        class="premium-alert premium-alert--error mb-6"
      >
        <div class="premium-alert-content">
          <div class="alert-icon-wrapper">
            <v-icon size="28" class="alert-icon">mdi-alert-octagon</v-icon>
          </div>
          <div class="alert-body">
            <h4 class="alert-title premium-alert-title">
              <span>Profile Status Check</span>
            </h4>
            <p class="premium-alert-message mb-0">{{ ProfileMessage }}</p>
          </div>
        </div>
      </v-alert>

      <!-- Profile Success Alert - Premium Success -->
      <v-alert
        v-if="ProfileFlag && ProfileMessage && IsProfileRequired > 0"
        prominent
        border="left"
        class="premium-alert premium-alert--success mb-6"
      >
        <div class="premium-alert-content">
          <div class="alert-icon-wrapper">
            <v-icon size="28" class="alert-icon"
              >mdi-check-circle-outline</v-icon
            >
          </div>
          <div class="alert-body">
            <h4 class="alert-title premium-alert-title">
              <span>Profile Status</span>
            </h4>
            <p class="premium-alert-message mb-0">{{ ProfileMessage }}</p>
          </div>
        </div>
      </v-alert>

      <!-- Required Fields Note -->
      <v-card class="required-note-card mb-6" elevation="0">
        <div class="required-note-content">
          <div class="required-note-icon">
            <v-icon color="white" size="24">mdi-alert-circle</v-icon>
          </div>
          <div class="required-note-text">
            <p class="required-note-label mb-0">
              <span class="asterisk">*</span> indicates required field
            </p>
          </div>
        </div>
      </v-card>
      <div class="d-flex justify-content-center mb-3" v-if="LoadingFlag">
        <b-spinner variant="primary" type="grow" label="Loading..."></b-spinner>
      </div>
      <v-container v-if="StartupLoadingFlag">
        <v-row wrap>
          <v-col cols="12" md="4" v-for="n in 6" :key="n">
            <v-skeleton-loader type="paragraph"></v-skeleton-loader>
          </v-col>
        </v-row>
      </v-container>
      <v-container v-if="!StartupLoadingFlag">
        <v-dialog v-model="addEducationDetailsPrompt" persistent width="auto">
          <add-education-details
            pageDescription="Add education details"
            :addEducationDetailsPrompt="addEducationDetailsPrompt"
            @hideAddRecordPrompt="addNewRow($event)"
            v-if="addEducationDetailsPrompt"
          ></add-education-details>
        </v-dialog>
        <v-dialog v-model="addRecordPrompt" persistent max-width="80%">
          <upload-profile-picture
            pageTitle="Change Profile Picture"
            pageDescription=""
            :addRecordPrompt="addRecordPrompt"
            :currentPhoto="Member.MemberImage"
            @hideAddRecordPrompt="changeProfilePhoto"
            v-if="addRecordPrompt"
          ></upload-profile-picture>
        </v-dialog>
        <v-form
          ref="form1"
          v-model="valid1"
          lazy-validation
          v-on:submit.prevent="confirmAlert"
        >
          <!-- Personal Details Card -->
          <v-card elevation="3" outlined class="form-card section-personal">
            <div class="section-header header-personal">
              <v-icon color="white" size="24">mdi-account-details</v-icon>
              <div>
                <h2 class="section-title mb-1">Personal Details</h2>
                <p class="section-subtitle mb-0">
                  Update your personal information
                </p>
              </div>
            </div>

            <!-- Profile Status and Missing Steps Section -->
            <v-alert
              v-if="Member && Object.keys(Member).length > 0"
              outlined
              class="profile-status-alert ma-4"
              :color="Member.ProfileUpdatedColor || 'info'"
              :icon="
                Member.ProfileStatusFlag
                  ? 'mdi-check-circle'
                  : 'mdi-alert-circle'
              "
            >
              <v-row class="align-center">
                <v-col cols="12" md="6">
                  <h4 class="profile-status-title mb-2">Profile Status</h4>
                  <v-chip :color="Member.ProfileUpdatedColor" dark class="mt-2">
                    {{ Member.ProfileUpdatedTxt }}
                  </v-chip>
                </v-col>
                <v-col cols="12" md="6">
                  <h4 class="missing-steps-title mb-2">Missing Information</h4>
                  <div
                    v-if="
                      Member.MissingSteps && Member.MissingSteps.trim() !== ''
                    "
                    class="missing-steps-list"
                  >
                    <v-chip
                      small
                      class="ma-1"
                      color="warning"
                      text-color="white"
                      icon="mdi-alert-circle"
                    >
                      {{ Member.MissingSteps }}
                    </v-chip>
                  </div>
                  <p v-else class="text-success mb-0">
                    <v-icon small color="success" class="mr-1"
                      >mdi-check-circle</v-icon
                    >
                    All information complete!
                  </p>
                </v-col>
              </v-row>
            </v-alert>

            <v-container>
              <v-row wrap>
                <!-- Member Name -->
                <v-col cols="12" sm="6" lg="3" md="3">
                  <div class="info-field">
                    <label class="info-label">Member Name</label>
                    <p class="info-value">{{ Member.Fullname || "N/A" }}</p>
                    <p class="info-value">{{ Member.MembershipId || "N/A" }}</p>
                  </div>
                </v-col>

                <!-- Profile Photo -->
                <v-col
                  cols="12"
                  sm="6"
                  lg="3"
                  md="3"
                  justify="center"
                  align="center"
                >
                  <div class="info-field">
                    <label class="info-label">
                      <v-icon small class="mr-2" color="primary"
                        >mdi-image</v-icon
                      >Profile Photo
                    </label>
                    <v-img
                      v-if="Member.MemberImage"
                      width="100"
                      height="100"
                      :src="Member.MemberImage"
                      class="member-photo mt-2"
                    />
                    <div v-else class="placeholder-photo mt-2"></div>
                    <br />
                    <v-btn
                      icon
                      small
                      color="primary"
                      @click.prevent="addRecordPrompt = true"
                      class="mt-2"
                    >
                      <v-icon>mdi-cloud-upload</v-icon>
                    </v-btn>
                  </div>
                </v-col>

                <!-- Aadhaar Verification Details -->
                <v-col cols="12" sm="6" lg="3" md="3">
                  <div class="info-field">
                    <label class="info-label">
                      <v-icon small class="mr-2" color="primary"
                        >mdi-shield-check</v-icon
                      >Aadhaar Verification
                    </label>
                    <v-chip
                      :color="Member.AadharNoIconColor || 'grey'"
                      dark
                      small
                      class="mt-2 mb-2"
                    >
                      {{ Member.AadharNoVerifiedTxt || "Not Verified" }}
                    </v-chip>
                    <div
                      v-if="Member.AadharNumber"
                      class="aadhaar-details-card mt-2 pa-2"
                    >
                      <small class="text-muted d-block mb-1"
                        >Aadhaar Number</small
                      >
                      <p class="font-weight-medium mb-2">
                        {{ maskAadharNumber(Member.AadharNumber) }}
                      </p>
                      <small class="text-muted d-block mb-1"
                        >Verification Status</small
                      >
                      <p class="font-weight-medium mb-0">
                        <v-icon
                          small
                          :color="Member.AadharNoIconColor || 'grey'"
                          class="mr-1"
                        >
                          {{
                            Member.AadharVerifiedFlag
                              ? "mdi-check-circle"
                              : "mdi-alert-circle"
                          }}
                        </v-icon>
                        {{ Member.AadharVerifiedTxt || "Not Verified" }}
                      </p>
                    </div>
                  </div>
                </v-col>

                <!-- Date of Birth (Read-only) -->
                <v-col cols="12" sm="6" lg="3" md="3">
                  <div class="info-field">
                    <label class="info-label">Date of Birth</label>
                    <p class="info-value">{{ Member.DobTxt || "N/A" }}</p>
                  </div>
                </v-col>

                <!-- JCI Member Since (Date Picker - Editable) -->
                <v-col cols="12" sm="6" lg="3" md="3">
                  <label class="info-label">
                    <span class="text-danger">*</span> JCI Member Since
                  </label>
                  <v-dialog
                    ref="dialog2"
                    v-model="DateModal2"
                    :return-value.sync="Member.JoiningDate"
                    persistent
                    width="290px"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="Member.JoiningDate"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                        outlined
                        dense
                        placeholder="Select date"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="Member.JoiningDate"
                      scrollable
                      :max="DateMax"
                    >
                      <v-spacer></v-spacer>
                      <v-btn text color="primary" @click="DateModal2 = false"
                        >Cancel</v-btn
                      >
                      <v-btn
                        text
                        color="primary"
                        @click="$refs.dialog2.save(Member.JoiningDate)"
                        >OK</v-btn
                      >
                    </v-date-picker>
                  </v-dialog>
                </v-col>

                <!-- Email Address -->
                <v-col cols="12" sm="6" lg="3" md="3">
                  <div class="info-field">
                    <label class="info-label">Email Address</label>
                    <p class="info-value">{{ Member.EmailId || "N/A" }}</p>
                  </div>
                </v-col>

                <!-- Mobile Number -->
                <v-col cols="12" sm="6" lg="3" md="3">
                  <div class="info-field">
                    <label class="info-label">Mobile Number</label>
                    <p class="info-value">{{ Member.MobileNo || "N/A" }}</p>
                  </div>
                </v-col>

                <!-- Street Address Line 1 -->
                <v-col cols="12" sm="6" lg="3" md="3">
                  <div class="info-field">
                    <label class="info-label">Street Address (Line 1)</label>
                    <p class="info-value">{{ Member.Street1 || "N/A" }}</p>
                  </div>
                </v-col>

                <!-- Street Address Line 2 -->
                <v-col cols="12" sm="6" lg="3" md="3">
                  <div class="info-field">
                    <label class="info-label">Street Address (Line 2)</label>
                    <p class="info-value">{{ Member.Street2 || "N/A" }}</p>
                  </div>
                </v-col>

                <!-- Locality/Area -->
                <v-col cols="12" sm="6" lg="3" md="3">
                  <div class="info-field">
                    <label class="info-label">Locality/Area</label>
                    <p class="info-value">{{ Member.Area || "N/A" }}</p>
                  </div>
                </v-col>

                <!-- State -->
                <v-col cols="12" sm="6" lg="3" md="3">
                  <div class="info-field">
                    <label class="info-label">State</label>
                    <p class="info-value">{{ Member.StateName || "N/A" }}</p>
                  </div>
                </v-col>

                <!-- District -->
                <v-col cols="12" sm="6" lg="3" md="3">
                  <div class="info-field">
                    <label class="info-label">District</label>
                    <p class="info-value">{{ Member.DistrictName || "N/A" }}</p>
                  </div>
                </v-col>

                <!-- City -->
                <v-col cols="12" sm="6" lg="3" md="3">
                  <div class="info-field">
                    <label class="info-label">City</label>
                    <p class="info-value">{{ Member.CityName || "N/A" }}</p>
                  </div>
                </v-col>

                <!-- Postal Code -->
                <v-col cols="12" sm="6" lg="3" md="3">
                  <div class="info-field">
                    <label class="info-label">Postal Code</label>
                    <p class="info-value">{{ Member.Pincode || "N/A" }}</p>
                  </div>
                </v-col>

                <!-- Personal Website -->
                <v-col cols="12" sm="6" lg="3" md="3">
                  <div class="info-field">
                    <label class="info-label">Personal Website</label>
                    <p class="info-value">{{ Website || "N/A" }}</p>
                  </div>
                </v-col>
              </v-row>
            </v-container>
          </v-card>
          <br />
          <br />
          <!-- Education Details Card -->
          <v-card elevation="3" outlined class="form-card section-education">
            <div class="section-header header-education">
              <v-icon color="white" size="24">mdi-school</v-icon>
              <div>
                <h2 class="section-title mb-1">
                  <span class="text-danger">*</span> Education Details
                </h2>
                <p class="section-subtitle mb-0">Your educational background</p>
              </div>
            </div>
            <v-container>
              <v-row wrap>
                <v-col cols="12" sm="12" lg="12" md="12">
                  <div class="table-wrapper">
                    <v-data-table
                      :headers="tableColumns2"
                      :items="EducationDetails"
                      hide-default-footer
                      disable-sort
                      class="professional-table"
                    >
                      <template v-slot:no-data>
                        <div class="table-empty-state">
                          <v-icon size="48" color="primary" class="empty-icon"
                            >mdi-book-open-outline</v-icon
                          >
                          <p>No education details added yet</p>
                          <v-btn
                            color="primary"
                            small
                            @click.prevent="addEducationDetailsPrompt = true"
                          >
                            <v-icon left small>mdi-plus</v-icon>Add Education
                          </v-btn>
                        </div>
                      </template>
                      <template v-slot:[`item.action`]="{ item, index }">
                        <div class="action-buttons">
                          <v-btn
                            icon
                            small
                            color="primary"
                            @click.prevent="addEducationDetailsPrompt = true"
                            title="Add"
                          >
                            <v-icon>mdi-plus-circle</v-icon>
                          </v-btn>
                          <v-btn
                            icon
                            small
                            color="error"
                            @click="deleteRow(index, item)"
                            title="Delete"
                          >
                            <v-icon>mdi-delete-circle-outline</v-icon>
                          </v-btn>
                        </div>
                      </template>
                    </v-data-table>
                  </div>
                </v-col>
              </v-row>
            </v-container>
          </v-card>
          <br />
          <br />
          <!-- Event Details Card -->
          <v-card elevation="3" outlined class="form-card section-event">
            <div class="section-header header-event">
              <v-icon color="white" size="24">mdi-briefcase</v-icon>
              <div>
                <h2 class="section-title mb-1">Event Details</h2>
                <p class="section-subtitle mb-0">
                  Your background and participation history
                </p>
              </div>
            </div>
            <v-container>
              <v-row wrap>
                <v-col cols="12" sm="6" lg="3" md="3">
                  <label class="info-label">
                    <v-icon small class="mr-1"
                      >mdi-badge-account-horizontal</v-icon
                    ><span class="text-danger">*</span> High position held in
                    JCII
                  </label>
                  <v-text-field
                    v-model="HighestPosition"
                    :rules="HighestPositionRules"
                    hint="Enter the High Position"
                    outlined
                    dense
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6" lg="3" md="3">
                  <label>
                    <h6>
                      <span class="text-danger">*</span> High position held Year
                    </h6>
                  </label>
                  <v-text-field
                    v-model="HighestPositionYear"
                    :rules="HighestPositionYearRules"
                    :maxlength="4"
                    :counter="4"
                    outlined
                    dense
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6" lg="3" md="3">
                  <label>
                    <h6><span class="text-danger">*</span> Your Profession</h6>
                  </label>
                  <v-text-field
                    v-model="ProfessionDetails"
                    :rules="ProfessionRules"
                    hint="Enter the Profession"
                    outlined
                    dense
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6" lg="3" md="3">
                  <label>
                    <h6>Failed</h6>
                  </label>
                  <v-switch inset v-model="FailedStatus">
                    <template v-slot:label>
                      <span>
                        {{ FailedStatus ? "Yes" : "No" }}
                      </span>
                    </template>
                  </v-switch>
                </v-col>
                <v-col cols="12" sm="6" lg="3" md="3" v-if="TrainerTypeFlag">
                  <label>
                    <h6>Trainer Type</h6>
                  </label>
                  <v-autocomplete
                    :reduce="(option) => option.value"
                    :loading="TrainerTypeOptionsLoading"
                    :items="TrainerTypeOptions"
                    :rules="TrainerTypeRules"
                    v-model="TrainerType"
                    dense
                    clearable
                    required
                    outlined
                  ></v-autocomplete>
                </v-col>
                <v-col cols="12" sm="6" lg="3" md="3">
                  <label>
                    <h6>
                      Have you attended earlier? if yes, Year
                      <small>(optional)</small>
                    </h6>
                  </label>
                  <v-text-field
                    v-model="AlreadyAttendedYear"
                    hint="Enter the Year"
                    outlined
                    dense
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6" lg="3" md="3">
                  <label>
                    <h6>Are you a Transferred Member from other LO</h6>
                  </label>
                  <v-switch inset v-model="TransferredStatus">
                    <template v-slot:label>
                      <span>
                        {{ TransferredStatus ? "Yes" : "No" }}
                      </span>
                    </template>
                  </v-switch>
                </v-col>
                <v-col cols="12" sm="6" lg="3" md="3" v-if="TransferredStatus">
                  <label>
                    <h6><span class="text-danger">*</span> Zone</h6>
                  </label>
                  <v-autocomplete
                    :reduce="(option) => option.value"
                    :loading="TransferredZoneCodeOptionsLoading"
                    :items="TransferredZoneCodeOptions"
                    v-model="TransferredZoneCode"
                    dense
                    clearable
                    required
                    outlined
                  ></v-autocomplete>
                </v-col>
                <v-col cols="12" sm="6" lg="3" md="3" v-if="TransferredStatus">
                  <label>
                    <h6><span class="text-danger">*</span> LO</h6>
                  </label>
                  <v-autocomplete
                    :reduce="(option) => option.value"
                    :loading="TransferredLomCodeOptionsLoading"
                    :items="TransferredLomCodeOptions"
                    v-model="TransferredLomCode"
                    dense
                    clearable
                    outlined
                  ></v-autocomplete>
                </v-col>
              </v-row>
            </v-container>
          </v-card>
          <br />
          <br />
          <!-- Training Programs Card -->
          <v-card elevation="3" outlined class="form-card section-training">
            <div class="section-header header-training">
              <v-icon color="white" size="24">mdi-presentation-play</v-icon>
              <div>
                <h2 class="section-title mb-1">Training Programs</h2>
                <p class="section-subtitle mb-0">
                  Your participation in training programs
                </p>
              </div>
            </div>
            <v-container>
              <v-row wrap>
                <v-col cols="12" sm="12" lg="12" md="12">
                  <div class="table-wrapper">
                    <v-data-table
                      :headers="tableColumns1"
                      :items="TrainingDetails"
                      hide-default-footer
                      disable-sort
                      class="professional-table"
                    >
                      <template v-slot:[`item.Date`]="{ item }">
                        <v-dialog v-model="DateModal3" persistent width="290px">
                          <template v-slot:activator="{ on, attrs }">
                            <v-text-field
                              v-model="item.Date"
                              placeholder="Date"
                              readonly
                              v-bind="attrs"
                              v-on="on"
                              @click="openDatePicker(item)"
                              outlined
                              dense
                              :rules="TrainingDateRules"
                            ></v-text-field>
                          </template>
                          <v-date-picker
                            v-model="TrainingDate"
                            scrollable
                            :max="DateMax"
                          >
                            <v-spacer></v-spacer>
                            <v-btn
                              text
                              color="primary"
                              @click="DateModal3 = false"
                              >Cancel</v-btn
                            >
                            <v-btn
                              text
                              color="primary"
                              @click="changePicker(Selected)"
                              >OK</v-btn
                            >
                          </v-date-picker>
                        </v-dialog>
                      </template>
                      <template v-slot:[`item.Host`]="{ item }">
                        <v-text-field
                          required
                          v-model="item.Host"
                          placeholder="Host"
                          outlined
                          dense
                          :rules="TrainingHostRules"
                        ></v-text-field>
                      </template>
                      <template v-slot:[`item.PilotFaculty`]="{ item }">
                        <v-text-field
                          required
                          v-model="item.PilotFaculty"
                          placeholder="Pilot Faculty"
                          outlined
                          dense
                          :rules="TrainingPilotFacultyRules"
                        ></v-text-field>
                      </template>
                    </v-data-table>
                  </div>
                </v-col>
              </v-row>
            </v-container>
          </v-card>
          <br />
          <br />
          <!-- Thoughts & Expectations Card -->
          <v-card elevation="3" outlined class="form-card section-thoughts">
            <div class="section-header header-thoughts">
              <v-icon color="white" size="24">mdi-lightbulb-on</v-icon>
              <div>
                <h2 class="section-title mb-1">Your Thoughts & Expectations</h2>
                <p class="section-subtitle mb-0">
                  Share your goals and aspirations
                </p>
              </div>
            </div>
            <v-container>
              <v-row wrap>
                <v-col cols="12">
                  <label class="info-label mb-2">
                    <v-icon small class="mr-1">mdi-text-box</v-icon>Describe
                    yourself in three lines <small>(optional)</small>
                  </label>
                  <v-textarea
                    v-model="HobbiesDescription"
                    value
                    :maxlength="300"
                    :counter="300"
                    outlined
                    solo
                  ></v-textarea>
                </v-col>
                <v-col cols="12">
                  <label class="info-label mb-2">
                    <v-icon small class="mr-1">mdi-target</v-icon>In what way
                    you will keep up your commitment to JCI INDIA?
                    <small>(optional)</small>
                  </label>
                  <v-textarea
                    v-model="AttendingDescription"
                    value
                    :maxlength="300"
                    :counter="300"
                    outlined
                    solo
                  ></v-textarea>
                </v-col>
                <v-col cols="12">
                  <label class="info-label mb-2">
                    <v-icon small class="mr-1">mdi-rocket</v-icon>Outline your
                    future goal in training <small>(optional)</small>
                  </label>
                  <v-textarea
                    v-model="FeatureGoalDescription"
                    value
                    :maxlength="300"
                    :counter="300"
                    outlined
                    solo
                  ></v-textarea>
                </v-col>
                <v-col cols="12">
                  <label class="info-label mb-2">
                    <v-icon small class="mr-1">mdi-head-question</v-icon>What
                    are your expectations from this NTTTS
                    <small>(optional)</small>
                  </label>
                  <v-textarea
                    v-model="Expectations"
                    value
                    :maxlength="300"
                    :counter="300"
                    outlined
                    solo
                  ></v-textarea>
                </v-col>
                <v-col cols="12">
                  <label class="info-label mb-2">
                    <v-icon small class="mr-1">mdi-hand-heart</v-icon>How will
                    you contribute as a qualified National Trainer?
                    <small>(optional)</small>
                  </label>
                  <v-textarea
                    v-model="ContributeDescription"
                    value
                    :maxlength="300"
                    :counter="300"
                    outlined
                    solo
                  ></v-textarea>
                </v-col>
              </v-row>
            </v-container>
          </v-card>
          <br />
          <br />
          <!-- Plans Section Header -->
          <div class="plans-section-header">
            <h2 class="plans-title">
              <v-icon size="28" class="plans-icon">mdi-package-variant</v-icon>
              Available Plans
            </h2>
            <p class="plans-subtitle">Select your registration plan</p>
          </div>
          <v-dialog v-model="PreviewPlanFlag" width="500">
            <event-plan-details
              :Plan="PreviewPlanDetails"
              @hideDialog="hidePreviewPlanPopup($event)"
              v-if="PreviewPlanFlag"
            ></event-plan-details>
          </v-dialog>
          <v-row justify="center" class="ma-3">
            <div v-for="(item, index) in Plans" :key="index">
              <v-col cols="12" sm="4" md="4" lg="4">
                <div v-if="item.Recommended">
                  <div class="cards-box" v-if="item.PlanVisibleFlag">
                    <div class="cards popular">
                      <div class="cards-ribbon">
                        <span>most popular</span>
                      </div>
                      <div class="cards-title-bar">
                        <h3 class="cards-title">{{ item.PlanName }}</h3>
                        <h4
                          class="cards-subtitle"
                          v-if="item.PlanDescription != ''"
                        >
                          {{ item.PlanDescription }}.
                        </h4>
                      </div>
                      <div class="cards-price">
                        <h4 class="cards-price-amount">
                          {{ item.TotalAmountTxt }}
                          <small class="cards-price-validity">per head</small>
                        </h4>
                        <v-divider />
                        <v-btn
                          color="primary"
                          dark
                          @click.prevent="showPreviewPlanPopup(item)"
                          >Show Plan details</v-btn
                        >
                        <p></p>
                      </div>
                      <br />
                      <v-divider />
                      <div class="cards-action">
                        <button
                          class="cards-action-button"
                          :disabled="!valid1"
                          @click.prevent="confirmAlert(item)"
                        >
                          Pay now
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                <div v-else>
                  <div class="cards-box" v-if="item.PlanVisibleFlag">
                    <div class="cards">
                      <div class="cards-title-bar">
                        <h3 class="cards-title">{{ item.PlanName }}</h3>
                        <h4
                          class="cards-subtitle"
                          v-if="item.PlanDescription != ''"
                        >
                          {{ item.PlanDescription }}.
                        </h4>
                      </div>
                      <div class="cards-price">
                        <h4 class="cards-price-amount">
                          {{ item.TotalAmountTxt }}
                          <small class="cards-price-validity">per head</small>
                        </h4>
                        <v-divider />
                        <v-btn
                          color="primary"
                          dark
                          @click.prevent="showPreviewPlanPopup(item)"
                          >Show Plan details</v-btn
                        >
                        <p></p>
                      </div>
                      <br />
                      <v-divider />
                      <div class="cards-action">
                        <button
                          class="cards-action-button"
                          :disabled="!valid1"
                          @click.prevent="confirmAlert(item)"
                          :loading="SubmitFlag"
                        >
                          Pay now
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </v-col>
            </div>
          </v-row>
        </v-form>
      </v-container>
    </v-card-text>
  </div>
</template>

<script>
import common from "@/view/Common.vue";
import companyConfig from "@/company_config.json";
import Swal from "sweetalert2";
import AddEducationDetails from "@/view/pages/erp/events/nalanda-events/AddEducationDetails";
import UploadProfilePicture from "@/view/pages/profile/my-profile/UploadProfilePicture2.vue";
import EventPlanDetails from "@/view/pages/erp/events/natcon/EventPlanDetails.vue";

export default {
  mixins: [common],
  components: {
    AddEducationDetails,
    UploadProfilePicture,
    EventPlanDetails,
  },
  data() {
    return {
      valid1: true,
      valid2: true,
      LoadingFlag: false,
      StartupLoadingFlag: false,
      ProgressLoadingFlag: false,
      SubmitFlag: false,
      NotesFlag: false,

      PagePermissionFlag: false,
      PagePermissionTxt: "",

      uploadPercentage: 0,
      progessStart: 0,

      addRecordPrompt: false,
      addEducationDetailsPrompt: false,

      DateModal1: false,
      DateModal2: false,
      DateModal3: false,
      DateMax: new Date().toISOString().slice(0, 10),

      PreviewPlanFlag: false,
      PreviewPlanDetails: {},

      TrainingDate: "",
      Selected: {},

      EmailRules: [(v) => !!v || "Email is required"],
      MobileNoRules: [(v) => !!v || "Mobile No is required"],
      Street1Rules: [(v) => !!v || "Street1 is required"],
      PincodeRules: [(v) => !!v || "Pincode is required"],
      HighestPositionRules: [(v) => !!v || "Highest Position is required"],
      HighestPositionYearRules: [
        (v) => !!v || "Highest Position Held Year is required",
        (v) =>
          v.length >= 4 || "Highest Position Held Year is required 4 digits",
      ],
      ProfessionRules: [(v) => !!v || "Profession is required"],

      TrainingDateRules: [(v) => !!v || "Training Date is required"],
      TrainingHostRules: [(v) => !!v || "Host is required"],
      TrainingPilotFacultyRules: [(v) => !!v || "Pilot Faculty is required"],

      tableColumns1: [
        { text: "Name Of Training", value: "Name" },
        { text: "Date", value: "Date" },
        { text: "Host", value: "Host" },
        { text: "Pilot Faculty", value: "PilotFaculty" },
      ],

      tableColumns2: [
        { text: "Year", value: "YearTxt" },
        { text: "Qualification", value: "QualificationTxt" },
        { text: "Degree", value: "DegreeTxt" },
        { text: "Specilization", value: "SpecilizationTxt" },
        { text: "Institution", value: "Institution" },
        { text: "Action", value: "action" },
      ],

      Member: {},
      EducationDetails: [],
      Website: "",
      TransferredStatus: false,
      HighestPosition: "",
      HighestPositionYear: "",
      ProfessionDetails: "",
      AlreadyAttendedYear: "",
      FailedStatus: false,
      HobbiesDescription: "",
      FeatureGoalDescription: "",
      AttendingDescription: "",
      ContributeDescription: "",
      Expectations: "",
      Opinion: "",
      TrainingDetails: [
        {
          Name: "ZTWS",
          Date: "",
          Host: "",
          PilotFaculty: "",
        },
      ],

      TrainerTypeFlag: false,
      TrainerTypeRules: [],
      TrainerType: "",
      TrainerTypeOptions: [
        { value: "", text: "--Select Trainer--" },
        { value: 0, text: "No Trainer" },
        { value: 1, text: "Zone Trainer" },
        { value: 2, text: "National Trainer" },
      ],
      TrainerTypeOptionsLoading: false,

      TransferredZoneCodeRules: [],
      TransferredZoneCode: "",
      TransferredZoneCodeOptions: [],
      TransferredZoneCodeOptionsLoading: false,

      TransferredLomCodeRules: [],
      TransferredLomCode: "",
      TransferredLomCodeOptions: [],
      TransferredLomCodeOptionsLoading: false,

      StateCodeRules: [(v) => !!v || "State is required"],
      StateCode: "",
      StateCodeOptions: [],
      StateCodeOptionsLoading: false,

      DistrictCodeRules: [(v) => !!v || "District is required"],
      DistrictCode: "",
      DistrictCodeOptions: [],
      DistrictCodeOptionsLoading: false,

      CityCodeRules: [(v) => !!v || "City is required"],
      CityCode: "",
      CityCodeOptions: [],
      CityCodeOptionsLoading: false,

      Events: {},
      Plans: [],
      selectedPlan: {},

      EventId: "",
      EventTypeId: 3,
      ZoneCode: "",
      LomCode: "",
      MemberId: "",
      PageInfo: {},
      PageTitle: "",
      PageDescription: "",
      CurrentYearId: 0,
      JciYearCode: "",
      YearName: "",
      NextYearCode: "",
      NextYearName: "",
      FloatingButtonOptions: {},

      // Eligibility Check Fields
      EligibilityFlag: false,
      EligibilityMessage: "",
      ShowEligibilityAlert: false,
      IsProfileRequired: 0,
      ContinuousYearsCount: 0,

      // Profile Check Fields
      ProfileFlag: false,
      ProfileMessage: "",
      ShowProfileAlert: false,

      SelectedImage: {
        ImageType: "",
        ImageUrl: null,
      },
      MemberImage: {},
    };
  },
  computed: {},
  mounted() {},
  watch: {
    PageInfo: function () {
      console.log("watch PageInfo");
      this.LoadingFlag = false;
      var tr = this.PageInfo;
      console.log({ tr });
      var n1 = Object.keys(tr).length;
      console.log({ n1 });
      if (n1 > 0) {
        var PagePermissionFlag = tr.PagePermissionFlag;
        console.log({ PagePermissionFlag });
        this.PagePermissionFlag = PagePermissionFlag;

        if (!PagePermissionFlag) {
          this.$router.push("/access-denied");
        }

        var PagePermissionTxt = tr.PagePermissionTxt;
        console.log({ PagePermissionTxt });
        this.PagePermissionTxt = PagePermissionTxt;

        this.PageTitle = tr.PageTitle;
        this.PageDescription = tr.PageDescription;
        this.FloatingButtonOptions = tr.FloatingButtonOptions;

        var CurrentJciYearId = parseInt(tr.CurrentJciYearId);
        console.log({ CurrentJciYearId });
        this.JciYearCode = CurrentJciYearId;

        var YearName = parseInt(tr.CurrentJciYearName);
        console.log({ YearName });
        this.YearName = YearName;

        this.CurrentYearId = CurrentJciYearId;

        var MemberId = this.$session.get("MemberId");
        this.MemberId = MemberId;
        console.log({ MemberId });

        var ZoneId = this.$session.get("ZoneId");
        this.ZoneCode = ZoneId;
        console.log({ ZoneId });

        var LomId = this.$session.get("LomId");
        this.LomCode = LomId;
        console.log({ LomId });

        // Check Eligibility Status
        if (tr.EligibilityFlag !== undefined) {
          this.EligibilityFlag = tr.EligibilityFlag;
          this.EligibilityMessage = tr.EligibilityMessage || "";
          this.ShowEligibilityAlert = !tr.EligibilityFlag; // Show alert only if not eligible
          this.IsProfileRequired = tr.IsProfileRequired || 0; // Set profile requirement flag
          this.ContinuousYearsCount = tr.ContinuousYearsCount || 0; // Set continuous years count
          console.log(
            "EligibilityFlag=" +
              this.EligibilityFlag +
              ", EligibilityMessage=" +
              this.EligibilityMessage +
              ", IsProfileRequired=" +
              this.IsProfileRequired +
              ", ContinuousYearsCount=" +
              this.ContinuousYearsCount
          );
        }

        // Check Profile Status
        if (tr.ProfileFlag !== undefined) {
          this.ProfileFlag = tr.ProfileFlag;
          this.ProfileMessage = tr.ProfileMessage || "";
          this.ShowProfileAlert = !tr.ProfileFlag; // Show alert only if profile not updated
          console.log(
            "ProfileFlag=" +
              this.ProfileFlag +
              ", ProfileMessage=" +
              this.ProfileMessage
          );
        }

        if (PagePermissionFlag) {
          this.getZoneCodeOptions();
          this.getMemberDetails();
          this.getEventsDetails();
        }
      }
    },
    TransferredZoneCode: function () {
      console.log("watch TransferredZoneCode");
      this.LoadingFlag = true;
      this.TransferredLomCode = "";
      this.getLomCodeOptions();
    },
    TransferredZoneCodeOptions: function () {
      console.log("watch TransferredZoneCodeOptions");
      this.TransferredZoneCodeOptionsLoading = false;
    },
    TransferredLomCodeOptions: function () {
      console.log("watch LomCodeOptions");
      this.LoadingFlag = false;
      this.TransferredLomCodeOptionsLoading = false;
    },
    Member: function () {
      console.log("watch Member");
      var rows = this.Member;
      var n1 = Object.keys(rows).length;
      // var n1 = this.rows.length;
      console.log("n1=" + n1 + ", rows=" + JSON.stringify(rows));
      if (n1 > 0) {
        var StateCode = rows.StateId;
        console.log({ StateCode });
        this.StateCode = StateCode;
        this.getDistrictCodeOptions();
        var DistrictCode = rows.DistrictId;
        console.log({ DistrictCode });
        this.DistrictCode = DistrictCode;
        this.getCityCodeOptions();
        var CityCode = rows.CityId;
        console.log({ CityCode });
        this.CityCode = CityCode;
      }
    },
    StateCodeOptions: function () {
      console.log("watch StateCodeOptions");
      this.LoadingFlag = false;
      this.StateCodeOptionsLoading = false;
    },
    StateCode: function () {
      console.log("watch StateCode");
      this.LoadingFlag = true;
      this.getDistrictCodeOptions();
    },
    DistrictCodeOptions: function () {
      console.log("watch DistrictCodeOptions");
      this.LoadingFlag = false;
      this.DistrictCodeOptionsLoading = false;
    },
    DistrictCode: function () {
      console.log("watch DistrictCode");
      this.LoadingFlag = true;
      this.getCityCodeOptions();
    },
    CityCodeOptions: function () {
      console.log("watch CityCodeOptions");
      this.LoadingFlag = false;
      this.CityCodeOptionsLoading = false;
    },
  },
  methods: {
    maskAadharNumber(aadharNo) {
      if (!aadharNo) return "N/A";
      const aadharStr = aadharNo.toString();
      if (aadharStr.length < 4) return aadharStr;
      return "XXXX XXXX " + aadharStr.slice(-4);
    },
    openDatePicker(tr) {
      console.log("openDatePicker called");
      var n1 = Object.keys(tr).length;
      console.log("n1=" + n1 + ", tr=" + JSON.stringify(tr));
      if (n1 > 0) {
        this.Selected = tr;
        this.TrainingDate = tr.Date;
        this.DateModal3 = true;
      } else {
        var message = "Kindly select one data to preview";
        this.toast("error", message);
      }
    },
    changePicker(tr) {
      console.log("changePicker called");
      var n1 = Object.keys(tr).length;
      console.log("n1=" + n1 + ", tr=" + JSON.stringify(tr));
      if (n1 > 0) {
        var name = tr.Name;
        var idx = this.TrainingDetails.map((e) => e.Name).indexOf(name);
        console.log("idx=" + idx);
        if (idx >= 0) {
          this.TrainingDetails[idx].Date = this.TrainingDate;
          this.DateModal3 = false;
        }
      } else {
        var message = "Kindly select one data to preview";
        this.toast("error", message);
      }
    },
    backPage() {
      this.$router.go(-1);
    },
    validate() {
      if (this.$refs.form2.validate()) {
        this.snackbar = true;
      }
    },
    changeProfilePhoto(photo) {
      console.log("changeProfilePhoto called");
      this.addRecordPrompt = false;
      console.log("photo=" + JSON.stringify(photo));
      // this.$refs.ProfilePicture.files[0] = photo;
      if (photo != "") {
        this.Member.MemberImage = photo;
        this.MemberImageFlag = true;
      }
    },
    showPreviewPlanPopup(tr) {
      this.PreviewPlanDetails = tr;
      this.PreviewPlanFlag = true;
    },
    hidePreviewPlanPopup() {
      this.PreviewPlanDetails = {};
      this.PreviewPlanFlag = false;
    },
    resetForm() {
      this.Member = {};
      this.MemberId = "";
      this.MembershipId = "";
      this.EducationDetails = [];
      this.Website = "";
      this.TransferredStatus = false;
      this.HighestPosition = "";
      this.HighestPositionYear = "";
      this.ProfessionDetails = "";
      this.AlreadyAttendedYear = "";
      this.FailedStatus = false;
      this.ContributeDescription = "";
      this.Expectations = "";
    },
    resetValidation() {
      this.$refs.form2.resetValidation();
    },
    refreshPageData() {
      console.log("refreshPageData called");
      this.LoadingFlag = true;
      this.resetForm();
      var id = this.$route.query.id;
      console.log({ id });
      this.EventId = id;
      var url1 = "api/jci-program/show";
      var condition1 = {
        UserInterface: 1,
        Program: id,
      };
      this.getPageDetails("PageInfo", {}, url1, condition1);
    },
    getZoneCodeOptions() {
      console.log("getZoneCodeOptions called");
      var yearCode = this.JciYearCode;
      console.log("yearCode=" + yearCode);
      if (yearCode != "") {
        this.TransferredZoneCodeOptionsLoading = true;
        var selectbox1_source = "TransferredZoneCode";
        var selectbox1_destination = "TransferredZoneCodeOptions";
        var selectbox1_url = "api/year-wise-lom/zone-options";
        var selectbox1_conditions_array = {
          UserInterface: 2,
          YearCode: yearCode,
        };
        this.getSelectBoxOptions(
          selectbox1_source,
          selectbox1_destination,
          selectbox1_conditions_array,
          selectbox1_url
        );
      } else {
        var message = "";
        if (yearCode == "") {
          message += "Year should not be empty. ";
        }
        this.toast("error", message);
      }
    },
    getLomCodeOptions() {
      console.log("getLomCodeOptions called");
      var YearCode = this.JciYearCode;
      var ZoneCode = this.TransferredZoneCode;
      console.log("YearCode=" + YearCode + ", ZoneCode=" + ZoneCode);
      if (YearCode != "" && ZoneCode != "") {
        this.LoadingFlag = true;
        this.TransferredLomCodeOptionsLoading = true;
        var selectbox1_source = "TransferredLomCode";
        var selectbox1_destination = "TransferredLomCodeOptions";
        var selectbox1_url = "api/year-wise-lom/lom-options";
        var selectbox1_conditions_array = {
          UserInterface: 3,
          YearCode: YearCode,
          ZoneCode: ZoneCode,
          AllOption: 0,
        };
        this.getSelectBoxOptions(
          selectbox1_source,
          selectbox1_destination,
          selectbox1_conditions_array,
          selectbox1_url
        );
      } else {
        var message = "";
        if (YearCode == "") {
          message += "Year should not be empty. ";
        }
        if (ZoneCode == "") {
          message += "Zone should not be empty. ";
        }
        this.toast("error", message);
      }
    },
    getMemberDetails() {
      console.log("getMemberDetails is called");

      var MemberId = this.MemberId;
      var ZoneCode = this.ZoneCode;
      var LomCode = this.LomCode;
      console.log(
        "MemberId=" +
          MemberId +
          "; ZoneCode=" +
          ZoneCode +
          "; LomCode=" +
          LomCode
      );

      if (MemberId != "" && ZoneCode != "" && LomCode != "") {
        this.LoadingFlag = true;

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/members/search";
        var upload = {
          UserInterface: 4,
          MemberId: MemberId,
          ZoneCode: ZoneCode,
          LomCode: LomCode,
        };
        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        const thisIns = this;
        var output = "";
        var records = "";
        var successTxt = "";
        var errorTxt = "";
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            // thisIns.pageLoadingOff()
            thisIns.LoadingFlag = false;

            output = response.data.output;
            flag = response.data.flag;
            output = response.data.output;
            records = response.data.records;
            console.log("output=" + output + ", flag=" + flag);
            successTxt = response.data.success;
            errorTxt = response.data.error;
            console.log("successTxt=" + successTxt + ", errorTxt=" + errorTxt);

            if (flag == 1) {
              thisIns.Member = records;
              thisIns.EducationDetails = records.EducationDetails;
              console.log(
                "EducationDetails=" + JSON.stringify(records.EducationDetails)
              );
              thisIns.getStateCodeOptions();
            } else {
              thisIns.toast("error", output);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.LoadingFlag = false;
          });
      } else {
        var message = "";
        if (MemberId == "" || ZoneCode == "" || LomCode == "") {
          message += "Your session is expired. Kindly login again. ";
        }
        this.toast("error", message);
      }
    },
    getEventsDetails() {
      console.log("getEventsDetails is called");

      var EventCode = this.EventId;
      console.log("EventCode=" + EventCode);

      if (EventCode != "") {
        this.LoadingFlag = true;
        this.Events = {};
        this.Plans = [];

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/jci-program/plan/lists";
        var upload = {
          UserInterface: 2,
          Program: EventCode,
          ProgramType: this.EventTypeId,
        };
        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        const thisIns = this;
        var output = "";
        var records = "";
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            // thisIns.pageLoadingOff()
            thisIns.LoadingFlag = false;

            output = response.data.output;
            flag = response.data.flag;
            console.log("output=" + output + ", flag=" + flag);

            records = response.data.records;
            console.log({ records });

            if (flag == 1) {
              // thisIns.Events = records;
              thisIns.Plans = records.TableData;
            } else {
              thisIns.toast("error", output);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.LoadingFlag = false;
          });
      } else {
        var message = "";
        if (LomId == "") {
          message += "Your session is expired. Kindly login again. ";
        }
        this.toast("error", message);
      }
    },
    getStateCodeOptions() {
      console.log("getStateCodeOptions called");
      this.LoadingFlag = true;
      this.StateCodeOptionsLoading = true;
      var selectbox1_source = "StateCode";
      var selectbox1_destination = "StateCodeOptions";
      var selectbox1_url = "api/states/options";
      var selectbox1_conditions_array = {
        UserInterface: 1,
      };
      this.getSelectBoxOptions(
        selectbox1_source,
        selectbox1_destination,
        selectbox1_conditions_array,
        selectbox1_url
      );
    },
    getDistrictCodeOptions() {
      console.log("getDistrictCodeOptions called");
      var StateCode = this.StateCode;
      console.log({ StateCode });
      if (StateCode != "") {
        this.LoadingFlag = true;
        this.DistrictCodeOptionsLoading = true;
        var selectbox1_source = "DistrictCode";
        var selectbox1_destination = "DistrictCodeOptions";
        var selectbox1_url = "api/district/options";
        var selectbox1_conditions_array = {
          UserInterface: 1,
          StatesCode: StateCode,
        };
        this.getSelectBoxOptions(
          selectbox1_source,
          selectbox1_destination,
          selectbox1_conditions_array,
          selectbox1_url
        );
      }
    },
    getCityCodeOptions() {
      console.log("getCityCodeOptions called");
      var DistrictCode = this.DistrictCode;
      console.log({ DistrictCode });
      if (DistrictCode != "") {
        this.CityCodeOptionsLoading = true;
        var selectbox1_source = "CityCode";
        var selectbox1_destination = "CityCodeOptions";
        var selectbox1_url = "api/city/options";
        var selectbox1_conditions_array = {
          UserInterface: 2,
          DistrictCode: DistrictCode,
        };
        this.getSelectBoxOptions(
          selectbox1_source,
          selectbox1_destination,
          selectbox1_conditions_array,
          selectbox1_url
        );
      }
    },
    confirmAlert(selectedPlan) {
      console.log("confirmAlert called");
      var validate1 = this.$refs.form1.validate();
      console.log("validate1=" + validate1);

      var JoiningDate = this.Member.JoiningDate;
      var DOB = this.Member.DOB;
      var MemberImageFlag = this.Member.MemberImageFlag;
      console.log(
        "MemberImageFlag=" +
          MemberImageFlag +
          "; JoiningDate=" +
          JoiningDate +
          "; DOB=" +
          DOB
      );

      var SelectedImage = this.SelectedImage;

      if (!MemberImageFlag) {
        if (SelectedImage.ImageUrl != null && SelectedImage.ImageUrl != "") {
          MemberImageFlag = true;
        }
      }
      console.log("MemberImageFlag=" + MemberImageFlag);

      var flag =
        validate1 &&
        MemberImageFlag &&
        JoiningDate != "" &&
        DOB != "" &&
        JoiningDate != null &&
        DOB != null
          ? true
          : false;
      console.log("flag=" + flag);

      if (flag) {
        var EventPlanId = selectedPlan.EventPlanId;
        EventPlanId = EventPlanId == (null || undefined) ? "" : EventPlanId;
        console.log("EventPlanId=" + EventPlanId);

        this.selectedPlan = selectedPlan;

        var htmlTxt = "";
        htmlTxt += "<br/><b>Selected plan details</b>";
        htmlTxt += "<br/>Plan name <b>" + selectedPlan.PlanName + "</b>";
        htmlTxt +=
          "<br/>Total amount <b>" + selectedPlan.TotalAmountTxt + "</b>";
        htmlTxt += "<br/><br/>Do you want to continue?";
        // htmlTxt += "<br/> Title <b> " + TitleName + "</b>";

        Swal.fire({
          title: "Please verify all the information before proceeding.",
          text: "",
          icon: "warning",
          html: htmlTxt,
          showCancelButton: true,
          confirmButtonText: `Continue`,
          confirmButtonColor: "#3085d6",
          cancelButtonText: `Cancel`,
          cancelButtonColor: "#d33",
        }).then((result) => {
          /* Read more about isConfirmed, isDenied below */
          if (result.isConfirmed) {
            this.generateRequestData();
          } else {
            this.sweetAlert("error", "You cancelled the process", true);
          }
        });
      } else {
        var message = "";
        if (!validate1) {
          message += "Kindly fill the required fields. ";
        }
        if (!MemberImageFlag) {
          message += "Member image should not be empty. ";
        }
        if (JoiningDate == "" || JoiningDate == null) {
          message += "Member Joining Date should not be empty. ";
        }
        if (DOB == "" || DOB == null) {
          message += "Member DOB should not be empty. ";
        }
        console.log("message=" + message);
        this.sweetAlert("error", message, false);
      }
    },
    flip(x, y) {
      console.log("flip x=" + x + ", y=" + y);
      this.$refs.cropper.flip(x, y);
    },
    rotate(angle) {
      console.log("rotate=" + angle);
      this.$refs.cropper.rotate(angle);
    },
    addImage(id) {
      console.log("addImage called");
      console.log({ id });
      switch (id) {
        case 1:
          var file = this.MemberImage;
          console.log({ file });
          var src = URL.createObjectURL(file);
          console.log({ src });
          if (this.SelectedImage.src) {
            URL.revokeObjectURL(this.SelectedImage.src);
          }
          var FileType = file.type;
          console.log({ FileType });
          this.SelectedImage = {
            ImageType: FileType,
            ImageUrl: src,
            UploadedImage: file,
          };
          break;

        default:
          break;
      }
    },
    deleteImage(id) {
      console.log("deleteImage called");
      console.log({ id });
      switch (id) {
        case 1:
          this.SelectedImage = {
            ImageType: null,
            ImageUrl: null,
          };
          this.MemberImage = {};
          break;

        default:
          break;
      }
    },
    getCroppedImage() {
      console.log("getCroppedImage called");

      var validate1 = this.$refs.form1.validate();
      console.log("validate1=" + validate1);

      var selectedPlan = this.selectedPlan;
      var EventPlanId = selectedPlan.EventPlanId;
      EventPlanId = EventPlanId == (null || undefined) ? "" : EventPlanId;
      console.log("EventPlanId=" + EventPlanId);

      var TrainerType = this.TrainerType == "" ? 0 : this.TrainerType;

      if (EventPlanId != "" && validate1) {
        var TotalAmount = parseFloat(selectedPlan.TotalAmount);
        console.log("TotalAmount=" + TotalAmount);

        this.Member.StateId = this.StateCode;
        this.Member.DistrictId = this.DistrictCode;
        this.Member.CityId = this.CityCode;

        var TransferredStatus = this.TransferredStatus ? 1 : 0;
        var FailedStatus = this.FailedStatus ? 1 : 0;

        let upload = new FormData();

        upload.append("UserInterface", 1);
        upload.append("RegistrationType", 3);
        upload.append("Event", this.EventId);
        upload.append("Plan", EventPlanId);
        upload.append("Lom", this.LomCode);
        upload.append("Member", this.Member.MemberId);
        upload.append("MobileNo", this.Member.MobileNo);
        upload.append("EmailId", this.Member.EmailId);
        upload.append("TotalCount", 1);
        upload.append("TotalAmount", TotalAmount);

        upload.append("Website", this.Website);
        upload.append("TransferredStatus", TransferredStatus);
        upload.append("TransferredLomCode", this.TransferredLomCode);
        upload.append("HighestPosition", this.HighestPosition);
        upload.append("HighestPositionYear", this.HighestPositionYear);
        upload.append("ProfessionDetails", this.ProfessionDetails);
        upload.append("TrainerType", TrainerType);
        upload.append("AlreadyAttendedYear", this.AlreadyAttendedYear);
        upload.append("FailedStatus", FailedStatus);
        upload.append("ContributeDescription", this.ContributeDescription);
        upload.append("ZtwsExpectations", this.Expectations);

        var EventData = {
          EducationDetails: this.EducationDetails,
          TrainingDetails: this.TrainingDetails,
          MemberDetails: this.Member,
        };

        upload.append("Form", JSON.stringify(EventData));

        var file = this.MemberImage;
        var FileName = file.name;
        console.log({ FileName });
        var FileType = file.type;
        console.log({ FileType });

        const { canvas } = this.$refs.cropper.getResult();
        if (canvas) {
          canvas.toBlob((blob) => {
            upload.append("ProfileImage", blob, FileName);
            this.submitForm(upload);
          }, FileType);
        } else {
          if (FileName) {
            message = "Kindly select image. ";
            this.sweetAlert("error", message, false);
          } else {
            upload.append("ProfileImage", null);
            this.submitForm(upload);
          }
        }
      } else {
        var message = "";
        if (!validate1) {
          message += "Kindly fill the required fields. ";
        }
        if (EventPlanId == "") {
          message += "Event plan id should not be empty. ";
        }
        // this.toast("error", message);
        this.sweetAlert("error", message, false);
      }
    },
    generateRequestData() {
      console.log("generateRequestData is called");
      var validate1 = this.$refs.form1.validate();
      console.log("validate1=" + validate1);

      var selectedPlan = this.selectedPlan;
      var EventPlanId = selectedPlan.EventPlanId;
      EventPlanId = EventPlanId == (null || undefined) ? "" : EventPlanId;
      console.log("EventPlanId=" + EventPlanId);

      var TrainerType = this.TrainerType == "" ? 0 : this.TrainerType;
      if (EventPlanId != "" && validate1) {
        var TotalAmount = parseFloat(selectedPlan.TotalAmount);
        console.log("TotalAmount=" + TotalAmount);

        this.Member.StateId = this.StateCode;
        this.Member.DistrictId = this.DistrictCode;
        this.Member.CityId = this.CityCode;

        var TransferredStatus = this.TransferredStatus ? 1 : 0;
        var FailedStatus = this.FailedStatus ? 1 : 0;

        var upload = {
          UserInterface: 8,
          RegistrationType: 3,
          Event: this.EventId,
          Plan: EventPlanId,
          Lom: this.LomCode,
          Member: this.Member.MemberId,
          MobileNo: this.Member.MobileNo,
          EmailId: this.Member.EmailId,
          TotalCount: 1,
          TotalAmount: TotalAmount,
          Website: this.Website,
          TransferredStatus: TransferredStatus,
          TransferredLomCode: this.TransferredLomCode,
          HighestPosition: this.HighestPosition,
          HighestPositionYear: this.HighestPositionYear,
          ProfessionDetails: this.ProfessionDetails,
          TrainerType: TrainerType,
          AlreadyAttendedYear: this.AlreadyAttendedYear,
          SpouseAttendingStatus: FailedStatus,
          HobbiesDescription: this.HobbiesDescription,
          AttendingDescription: this.AttendingDescription,
          FeatureGoalDescription: this.FeatureGoalDescription,
          Opinion: this.Opinion,
          Expectations: this.Expectations,
          ContributeDescription: this.ContributeDescription,
          EducationDetails: this.EducationDetails,
          TrainingDetails: this.TrainingDetails,
          MemberDetails: this.Member,
        };

        console.log("upload=" + JSON.stringify(upload));
        this.submitForm(upload);
      } else {
        var message = "";
        if (!validate1) {
          message += "Kindly fill the required fields. ";
        }
        if (EventPlanId == "") {
          message += "Event plan id should not be empty. ";
        }
        // this.toast("error", message);
        this.sweetAlert("error", message, false);
      }
    },
    submitForm(upload) {
      console.log("submitForm is called");

      this.ProgressLoadingFlag = true;

      var server_url = companyConfig.apiURL;
      var token = this.$session.get("token");
      token = token == (null || undefined) ? 0 : token;
      var add_url = server_url + "api/jci-program/order/create";
      console.log(
        "server_url=" + server_url + ", token=" + token + ", add_url=" + add_url
      );
      console.log("upload=" + JSON.stringify(upload));

      this.progessStart = 1;

      const thisIns = this;
      var output = "";
      var records = "";
      var flag = 0;

      this.$http({
        url: add_url,
        method: "POST",
        data: upload,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
        onUploadProgress: function (progressEvent) {
          this.uploadPercentage = parseInt(
            Math.round((progressEvent.loaded / progressEvent.total) * 100)
          );
        }.bind(this),
      })
        .then(function (response) {
          console.log("response=" + JSON.stringify(response));

          thisIns.ProgressLoadingFlag = false;
          thisIns.progessStart = 0;

          output = response.data.output;
          flag = response.data.flag;
          console.log("output=" + output + ", flag=" + flag);
          records = response.data.records;
          console.log({ records });

          if (flag == 1) {
            thisIns.Order = records.Order;
            thisIns.makePayment();
            // thisIns.sweetAlert("success", output, true);
          } else {
            // thisIns.toast("error", output);
            thisIns.sweetAlert("error", output, false);
          }
        })
        .catch(function (error) {
          console.log("error=" + error);
          thisIns.ProgressLoadingFlag = false;
        });
    },
    addNewRow(data) {
      console.log("addNewRow called");
      console.log("data=" + JSON.stringify(data));
      var dataFlag = data.InsertFlag;
      console.log("dataFlag=" + dataFlag);

      if (dataFlag) {
        this.EducationDetails.push(data);
        console.log(
          "EducationDetails=" + JSON.stringify(this.EducationDetails)
        );
      }
      this.addEducationDetailsPrompt = false;
    },
    deleteRow(index, tr) {
      console.log("deleteRow called");
      console.log({ index });
      console.log({ tr });
      if (index >= 0) {
        this.EducationDetails.splice(index, 1);
      }
    },
    makePayment() {
      console.log("makePayment");
      // ev.preventDefault();
      var order = this.Order;
      const thisIns = this;
      var rzp1 = new Razorpay({
        key: order.rzp_key,
        account_id: order.rzp_account_id,
        order_id: order.rzp_order_id,
        amount: order.rzp_amount,
        name: order.rzp_name,
        currency: "INR",
        description: order.rzp_description,
        image: order.rzp_image,
        theme: {
          color: order.rzp_color,
        },
        prefill: {
          name: order.customer_name,
          email: order.customer_email_id,
          contact: order.customer_mobile_no,
        },
        notes: {
          address: "",
        },
        // callback_url: "http://domain.com/#/about/" + this.order_id,
        redirect: false,
        handler: function (response) {
          console.log("razorpay_payment_id=" + response.razorpay_payment_id);
          thisIns.SubmitFlag = true;
          // thisIns.$store.dispatch(ADD_BODY_CLASSNAME, "page-loading");
          thisIns.pageRedirect({}, "/order/success");
        },
      });
      rzp1.open();
    },
  },
  beforeMount() {
    var CurrentJciYearId = this.$session.get("CurrentJciYearId");
    // CurrentJciYearId = 11;
    CurrentJciYearId =
      CurrentJciYearId == (null || undefined) ? "" : CurrentJciYearId;
    console.log("CurrentJciYearId=" + CurrentJciYearId);
    this.JciYearCode = CurrentJciYearId;
    this.refreshPageData();
    /*
    this.tableColumns1.push(
      { text: "Description", value: "Description" },
      { text: "Subscription Fees", value: "AmountTxt" },
      { text: "Nos", value: "Qty" },
      { text: "Sub Total", value: "SubTotal" }
      // { text: "Actions", value: "actions" }
    );
    */
  },
};
</script>
<style lang="scss">
// Color Palette (JCI Approved Colors)
$navy: #3a67b1;
$aqua: #0097d7;
$gold: #edbe38;
$seaform: #56bda3;
$text-90-black: #414042;
$text-off-black: #0a0f29;
$text-white: #ffffff;

$primary-color: $navy;
$secondary-color: $aqua;
$success-color: $seaform;
$warning-color: $gold;
$danger-color: #e74c3c;
$info-color: $aqua;
$light-bg: #f8fafc;
$bg-light: #f1f5f9;
$text-primary: $text-off-black;
$text-secondary: $text-90-black;
$border-color: #e2e8f0;
$gradient-main: linear-gradient(135deg, $navy 0%, $aqua 100%);

// Animations
@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideInLeft {
  from {
    opacity: 0;
    transform: translateX(-30px);
  }

  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes float {
  0%,
  100% {
    transform: translateY(0px);
  }

  50% {
    transform: translateY(-12px);
  }
}

@keyframes pulse {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(102, 126, 234, 0.7);
  }

  50% {
    box-shadow: 0 0 0 10px rgba(102, 126, 234, 0);
  }
}

@keyframes glow {
  0%,
  100% {
    text-shadow: 0 0 5px rgba(102, 126, 234, 0.5),
      0 0 10px rgba(102, 126, 234, 0.3);
  }

  50% {
    text-shadow: 0 0 10px rgba(102, 126, 234, 0.8),
      0 0 20px rgba(102, 126, 234, 0.5);
  }
}

@keyframes shimmer {
  0% {
    background-position: -1000px 0;
  }

  100% {
    background-position: 1000px 0;
  }
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

@keyframes cardBounce {
  0%,
  100% {
    transform: translateY(0);
  }

  50% {
    transform: translateY(-8px);
  }
}

@keyframes fadeInScale {
  from {
    opacity: 0;
    transform: scale(0.95);
  }

  to {
    opacity: 1;
    transform: scale(1);
  }
}

#ztws-events-create {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  min-height: 100vh;
  padding: 30px 15px;

  // ============================================
  // HEADER CARD
  // ============================================
  .header-card {
    background: linear-gradient(
      135deg,
      $navy 0%,
      $aqua 50%,
      $navy 100%
    ) !important;
    border-radius: 20px;
    overflow: hidden;
    animation: slideDown 0.8s cubic-bezier(0.34, 1.56, 0.64, 1);
    position: relative;
    border: none;
    box-shadow: 0 20px 60px rgba(58, 103, 177, 0.5),
      0 0 40px rgba(0, 151, 215, 0.3);

    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(
        45deg,
        transparent 30%,
        rgba(255, 255, 255, 0.1) 50%,
        transparent 70%
      );
      animation: shimmer 3s infinite;
    }

    .header-backdrop-effect {
      position: absolute;
      top: -50%;
      right: -50%;
      width: 200%;
      height: 200%;
      background: radial-gradient(
        circle,
        rgba(255, 255, 255, 0.15) 0%,
        transparent 70%
      );
      animation: rotate 25s linear infinite;
    }

    .header-content {
      position: relative;
      z-index: 2;
    }
  }

  .header-container {
    padding: 20px 0 !important;
  }

  .header-icon-box {
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.25) 0%,
      rgba(255, 255, 255, 0.15) 100%
    );
    backdrop-filter: blur(15px);
    border-radius: 18px;
    padding: 14px;
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.2),
      inset 0 1px 0 rgba(255, 255, 255, 0.4);
    animation: float 4s ease-in-out infinite;
    border: 2px solid rgba(255, 255, 255, 0.3);
  }

  .header-title {
    font-size: 36px;
    font-weight: 900;
    text-shadow: 2px 2px 8px rgba(0, 0, 0, 0.3), 0 0 20px rgba(0, 151, 215, 0.3);
    animation: slideInLeft 0.8s ease-out;
    letter-spacing: 0.8px;
    color: $text-white;
  }

  .header-subtitle {
    font-size: 17px;
    opacity: 0.98;
    animation: slideInLeft 1s ease-out 0.1s backwards;
    letter-spacing: 0.4px;
    color: $text-white;
    font-weight: 500;
  }

  .action-btn {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    font-weight: 700;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    background: rgba(255, 255, 255, 0.15) !important;
    border: 2px solid rgba(255, 255, 255, 0.3) !important;
    backdrop-filter: blur(10px);
    color: white !important;

    &:hover {
      transform: translateY(-4px);
      box-shadow: 0 12px 24px rgba(0, 0, 0, 0.3) !important;
      background: rgba(255, 255, 255, 0.25) !important;
    }
  }

  // ============================================
  // NOTES ALERT
  // ============================================
  .notes-alert {
    border-radius: 12px !important;
    background: linear-gradient(
      135deg,
      rgba(33, 150, 243, 0.12) 0%,
      rgba(119, 235, 255, 0.08) 100%
    ) !important;
    border-left: 5px solid $info-color !important;
    animation: slideDown 0.8s cubic-bezier(0.34, 1.56, 0.64, 1);
    box-shadow: 0 8px 24px rgba(33, 150, 243, 0.15) !important;
    padding: 16px 20px !important;
  }

  .alert-content {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .alert-title {
    font-size: 14px;
    font-weight: 700;
    color: $info-color;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    margin: 0;
  }

  .alert-list {
    margin: 12px 0 0 20px;
    font-size: 13px;
    color: $text-secondary;
    line-height: 1.8;

    li {
      margin-bottom: 8px;
      font-weight: 500;
    }
  }

  // ============================================
  // PREMIUM ALERTS
  // ============================================
  .premium-alert {
    border-radius: 16px !important;
    border: none !important;
    overflow: hidden;
    animation: slideDown 0.8s cubic-bezier(0.34, 1.56, 0.64, 1);
    padding: 0 !important;
    position: relative;
  }

  .premium-alert--error {
    background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%) !important;
    box-shadow: 0 12px 32px rgba(239, 68, 68, 0.3) !important;
    border-left: 5px solid #dc2626 !important;

    .alert-icon-wrapper {
      background: linear-gradient(135deg, #fca5a5 0%, #f87171 100%);
      border-right: 2px solid #dc2626;
    }

    .alert-icon {
      color: #dc2626;
      animation: pulse 2s ease-in-out infinite;
    }

    .premium-alert-title {
      color: #7f1d1d;
    }

    .premium-alert-message {
      color: #7f1d1d;
      font-weight: 600;
    }
  }

  .premium-alert--success {
    background: linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%) !important;
    box-shadow: 0 12px 32px rgba(34, 197, 94, 0.3) !important;
    border-left: 5px solid #16a34a !important;

    .alert-icon-wrapper {
      background: linear-gradient(135deg, #86efac 0%, #4ade80 100%);
      border-right: 2px solid #16a34a;
    }

    .alert-icon {
      color: #16a34a;
      animation: pulse 2s ease-in-out infinite;
    }

    .premium-alert-title {
      color: #166534;
    }

    .premium-alert-message {
      color: #166534;
      font-weight: 600;
    }
  }

  .premium-alert-content {
    display: flex;
    align-items: flex-start;
    gap: 16px;
    padding: 20px 24px;
    width: 100%;
  }

  .alert-icon-wrapper {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    border-radius: 12px;
    transition: all 0.3s ease;
  }

  .alert-icon {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .alert-body {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .premium-alert-title {
    font-size: 15px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.8px;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .premium-alert-message {
    font-size: 14px;
    font-weight: 500;
    line-height: 1.6;
    color: rgba(0, 0, 0, 0.7);
  }

  // Old eligibility alerts (keeping for backwards compatibility if needed)
  .eligibility-alert {
    border-radius: 12px !important;
    background: linear-gradient(
      135deg,
      rgba(211, 47, 47, 0.12) 0%,
      rgba(229, 57, 53, 0.08) 100%
    ) !important;
    border-left: 5px solid $danger-color !important;
    animation: slideDown 0.8s cubic-bezier(0.34, 1.56, 0.64, 1);
    box-shadow: 0 8px 24px rgba(211, 47, 47, 0.15) !important;
    padding: 16px 20px !important;

    .alert-title {
      color: $danger-color;
    }

    p {
      color: $text-secondary;
      font-weight: 500;
    }
  }

  .eligibility-success-alert {
    border-radius: 12px !important;
    background: linear-gradient(
      135deg,
      rgba(76, 175, 80, 0.12) 0%,
      rgba(129, 199, 132, 0.08) 100%
    ) !important;
    border-left: 5px solid $success-color !important;
    animation: slideDown 0.8s cubic-bezier(0.34, 1.56, 0.64, 1);
    box-shadow: 0 8px 24px rgba(76, 175, 80, 0.15) !important;
    padding: 16px 20px !important;

    .alert-title {
      color: $success-color;
    }

    p {
      color: $text-secondary;
      font-weight: 500;
    }
  }

  // Eligibility warning text under disabled button
  .eligibility-warning-text {
    font-size: 12px;
    color: #ef4444;
    text-align: center;
    margin-top: 8px !important;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.4px;
  }

  // ============================================
  // REQUIRED NOTE CARD
  // ============================================
  .required-note-card {
    border-radius: 12px !important;
    background: linear-gradient(
      135deg,
      rgba(58, 103, 177, 0.12) 0%,
      rgba(0, 151, 215, 0.08) 100%
    ) !important;
    border-left: 5px solid $navy !important;
    animation: slideDown 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
    box-shadow: 0 8px 24px rgba(58, 103, 177, 0.15) !important;
    padding: 16px 20px !important;
    position: relative;
    overflow: hidden;
    border: none !important;

    &::before {
      content: "";
      position: absolute;
      top: -50%;
      right: -50%;
      width: 200%;
      height: 200%;
      background: radial-gradient(
        circle,
        rgba(255, 255, 255, 0.08) 0%,
        transparent 70%
      );
      animation: rotate 20s linear infinite;
    }
  }

  .required-note-content {
    display: flex;
    align-items: center;
    gap: 16px;
    justify-content: center;
    position: relative;
    z-index: 1;
  }

  .required-note-icon {
    background: rgba(58, 103, 177, 0.2);
    border-radius: 50%;
    padding: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    box-shadow: 0 4px 12px rgba(58, 103, 177, 0.15);
    color: $navy;
  }

  .required-note-text {
    flex: 1;
    position: relative;
    z-index: 1;
  }

  .required-note-label {
    font-size: 14px;
    font-weight: 700;
    color: $navy;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    display: flex;
    align-items: center;
    gap: 8px;

    .asterisk {
      font-size: 18px;
      color: $gold;
      font-weight: 900;
      text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
    }
  }

  // ============================================
  // FORM CARDS
  // ============================================
  .form-card {
    border-radius: 18px !important;
    border: 2px solid rgba(58, 103, 177, 0.2);
    box-shadow: 0 12px 40px rgba(58, 103, 177, 0.15),
      0 0 30px rgba(0, 151, 215, 0.08);
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    animation: fadeInScale 0.6s ease-out;
    background: linear-gradient(
      to bottom,
      #ffffff 0%,
      #fafbff 50%,
      #f5f7ff 100%
    );
    position: relative;
    overflow: hidden;

    &::before {
      content: "";
      position: absolute;
      top: -50%;
      right: -50%;
      width: 200%;
      height: 200%;
      background: radial-gradient(
        circle,
        rgba(58, 103, 177, 0.05) 0%,
        transparent 70%
      );
      animation: rotate 30s linear infinite;
    }

    &:hover {
      transform: translateY(-12px);
      box-shadow: 0 20px 60px rgba(58, 103, 177, 0.25),
        0 0 50px rgba(0, 151, 215, 0.15);
      border-color: rgba(58, 103, 177, 0.4);

      .section-header {
        box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.8);
      }

      .section-title {
        animation: glow 2s ease-in-out infinite;
      }
    }

    .section-header {
      display: flex;
      align-items: center;
      padding: 24px;
      position: relative;
      z-index: 1;
      transition: all 0.3s ease;
      border-bottom: 3px solid;

      .v-icon {
        margin-right: 16px;
        flex-shrink: 0;
        animation: float 3s ease-in-out infinite;
      }
    }

    .section-title {
      font-size: 22px;
      font-weight: 900;
      margin: 0;
      letter-spacing: 0.5px;
      transition: all 0.3s ease;
      color: $text-white;
    }

    .section-subtitle {
      font-size: 13px;
      color: rgba(255, 255, 255, 0.9);
      margin: 4px 0 0 0;
      opacity: 0.9;
      font-weight: 500;
    }
  }

  // Info Field Styles
  .info-field {
    padding: 12px 0;
    border-bottom: 1px solid #e0e0e0;

    &:last-child {
      border-bottom: none;
    }
  }

  .info-label {
    display: block;
    font-size: 12px;
    font-weight: 600;
    color: #666;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 6px;
    line-height: 1.4;
  }

  .info-value {
    font-size: 16px;
    font-weight: 500;
    color: #333;
    margin: 0;
    word-wrap: break-word;
    line-height: 1.5;
  }

  .aadhaar-details-card {
    background: #f5f5f5;
    border-radius: 4px;
    border-left: 3px solid #1976d2;
  }

  // Personal Details - Navy
  .section-personal {
    .section-header {
      background: linear-gradient(135deg, $navy 0%, darken($navy, 5%) 100%);
      border-bottom-color: $aqua;

      &:hover {
        background: linear-gradient(
          135deg,
          darken($navy, 5%) 0%,
          darken($navy, 10%) 100%
        );
      }
    }
  }

  // Education Details - Navy to Aqua
  .section-education {
    .section-header {
      background: linear-gradient(135deg, $navy 0%, $aqua 100%);
      border-bottom-color: lighten($aqua, 10%);

      &:hover {
        background: linear-gradient(
          135deg,
          darken($navy, 5%) 0%,
          darken($aqua, 5%) 100%
        );
      }
    }
  }

  // Event Details - Aqua
  .section-event {
    .section-header {
      background: linear-gradient(135deg, $aqua 0%, darken($aqua, 5%) 100%);
      border-bottom-color: lighten($aqua, 15%);

      &:hover {
        background: linear-gradient(
          135deg,
          darken($aqua, 5%) 0%,
          darken($aqua, 10%) 100%
        );
      }
    }
  }

  // Training Programs - Seaform
  .section-training {
    .section-header {
      background: linear-gradient(
        135deg,
        $seaform 0%,
        darken($seaform, 5%) 100%
      );
      border-bottom-color: lighten($seaform, 10%);

      &:hover {
        background: linear-gradient(
          135deg,
          darken($seaform, 5%) 0%,
          darken($seaform, 10%) 100%
        );
      }
    }
  }

  // Thoughts & Expectations - Navy to Seaform
  .section-thoughts {
    .section-header {
      background: linear-gradient(135deg, $navy 0%, $seaform 100%);
      border-bottom-color: $gold;

      &:hover {
        background: linear-gradient(
          135deg,
          darken($navy, 5%) 0%,
          darken($seaform, 5%) 100%
        );
      }
    }
  }

  // ============================================
  // MEMBER PHOTO & INFO
  // ============================================
  .member-photo-container {
    padding: 16px;
    background: $bg-light;
    border-radius: 12px;
    border: 2px solid $border-color;
    transition: all 0.3s ease;

    &:hover {
      border-color: $primary-color;
      box-shadow: 0 8px 20px rgba(102, 126, 234, 0.2);
    }

    .member-photo {
      border-radius: 12px;
      overflow: hidden;
    }
  }

  .placeholder-photo {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 140px;
    background: linear-gradient(
      135deg,
      rgba(102, 126, 234, 0.1) 0%,
      rgba(118, 75, 162, 0.1) 100%
    );
    border-radius: 12px;
  }

  .member-photo {
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    border: 3px solid $primary-color;
    transition: all 0.3s ease;

    &:hover {
      transform: scale(1.05);
      box-shadow: 0 8px 20px rgba(102, 126, 234, 0.3);
    }
  }

  .member-name {
    font-size: 16px;
    font-weight: 700;
    color: $primary-color;
    margin: 0 0 4px 0;
  }

  // ============================================
  // PROFESSIONAL TABLE
  // ============================================
  .table-wrapper {
    overflow-x: auto;
    border-radius: 16px;
    box-shadow: 0 12px 40px rgba(58, 103, 177, 0.2),
      0 0 30px rgba(0, 151, 215, 0.1);
    background: linear-gradient(
      to bottom,
      #ffffff 0%,
      #fafbff 50%,
      #f5f7ff 100%
    );
    padding: 0;
    border: 2px solid rgba(58, 103, 177, 0.15);
    animation: fadeInScale 0.7s ease-out;
    position: relative;
    transition: all 0.3s ease;

    &:hover {
      box-shadow: 0 16px 60px rgba(58, 103, 177, 0.3),
        0 0 40px rgba(0, 151, 215, 0.15);
      border-color: rgba(58, 103, 177, 0.3);
    }

    .professional-table {
      ::v-deep {
        .v-data-table__wrapper {
          padding: 0;
        }

        table {
          border-collapse: collapse;

          thead tr {
            background: linear-gradient(
              135deg,
              rgba(58, 103, 177, 0.12) 0%,
              rgba(0, 151, 215, 0.12) 100%
            );
            border-bottom: 3px solid $navy;
            position: relative;

            &::after {
              content: "";
              position: absolute;
              bottom: -3px;
              left: 0;
              right: 0;
              height: 3px;
              background: linear-gradient(
                90deg,
                $navy 0%,
                $aqua 50%,
                $seaform 100%
              );
            }

            th {
              font-size: 13px !important;
              font-weight: 800 !important;
              color: $navy !important;
              text-transform: uppercase !important;
              letter-spacing: 0.5px !important;
              padding: 18px 14px !important;
              border: none !important;
              background: transparent !important;

              &:first-child {
                border-radius: 12px 0 0 0;
              }
            }
          }

          tbody tr {
            border-bottom: 1px solid rgba(58, 103, 177, 0.08);
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
            position: relative;

            &:hover {
              background: linear-gradient(
                90deg,
                rgba(58, 103, 177, 0.08) 0%,
                rgba(0, 151, 215, 0.08) 100%
              ) !important;
              transform: translateX(4px);
              box-shadow: inset 0 2px 8px rgba(58, 103, 177, 0.1);

              td {
                color: $navy !important;
                font-weight: 600;
              }
            }

            td {
              font-size: 14px !important;
              color: $text-90-black !important;
              padding: 16px 14px !important;
              border: none !important;
              transition: all 0.2s ease;
              font-weight: 500;
            }

            &:nth-child(odd) {
              background: rgba(58, 103, 177, 0.02);
            }
          }
        }
      }
    }
  }

  .table-empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 50px 24px;
    gap: 16px;
    background: linear-gradient(
      135deg,
      rgba(102, 126, 234, 0.08) 0%,
      rgba(118, 75, 162, 0.08) 100%
    );
    border-radius: 14px;
    border: 3px dashed rgba(102, 126, 234, 0.25);
    animation: fadeInScale 0.6s ease-out;

    .empty-icon {
      color: $primary-color;
      font-size: 48px;
      animation: float 3s ease-in-out infinite;
    }

    p {
      font-size: 15px;
      color: $text-secondary;
      margin: 0;
      font-weight: 600;
    }

    .add-btn {
      margin-top: 12px;
    }
  }

  .action-buttons {
    display: flex;
    gap: 4px;
    align-items: center;
    justify-content: flex-start;
  }

  // ============================================
  // PLAN CARDS
  // ============================================
  .cards {
    background: linear-gradient(135deg, #ffffff 0%, #fafbff 100%);
    border-radius: 18px;
    box-shadow: 0 12px 40px rgba(58, 103, 177, 0.15),
      0 0 30px rgba(0, 151, 215, 0.08);
    flex: 1;
    margin: 12px 8px;
    padding: 24px;
    padding-top: 45px;
    height: 440px;
    width: 280px;
    position: relative;
    text-align: center;
    transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
    border: 2px solid rgba(58, 103, 177, 0.15);
    animation: slideInUp 0.7s ease-out;
    overflow: hidden;

    &::before {
      content: "";
      position: absolute;
      top: -50%;
      right: -50%;
      width: 200%;
      height: 200%;
      background: radial-gradient(
        circle,
        rgba(58, 103, 177, 0.08) 0%,
        transparent 70%
      );
      animation: rotate 35s linear infinite;
    }

    &:hover {
      transform: translateY(-16px) scale(1.02);
      box-shadow: 0 20px 60px rgba(58, 103, 177, 0.28),
        0 0 50px rgba(0, 151, 215, 0.18);
      border-color: rgba(58, 103, 177, 0.3);

      .cards-title {
        animation: glow 2s ease-in-out infinite;
      }

      .cards-price-amount {
        animation: pulse 2s ease-in-out infinite;
      }
    }
  }

  .cards.popular {
    margin-top: -20px;
    height: 460px;
    padding: 28px;
    padding-top: 55px;
    width: 280px;
    box-shadow: 0 16px 60px rgba(0, 151, 215, 0.35),
      0 0 60px rgba(237, 190, 56, 0.2);
    background: linear-gradient(135deg, #ffffff 0%, #f0f8ff 50%, #f5faff 100%);
    border: 2px solid rgba(0, 151, 215, 0.35);
    position: relative;

    &::after {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(
        45deg,
        transparent 30%,
        rgba(0, 151, 215, 0.05) 50%,
        transparent 70%
      );
      animation: shimmer 4s infinite;
    }

    &:hover {
      transform: translateY(-20px) scale(1.04);
      box-shadow: 0 24px 80px rgba(0, 151, 215, 0.45),
        0 0 80px rgba(237, 190, 56, 0.3);
      border-color: rgba(0, 151, 215, 0.5);
    }

    .cards-title {
      color: $aqua;
      font-size: 22px;
      font-weight: 900;
      background: linear-gradient(135deg, $aqua 0%, $seaform 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }

    .cards-price-amount {
      color: $aqua;
      font-size: 40px;
      background: linear-gradient(135deg, $aqua 0%, $seaform 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }

    .cards-action-button {
      background: linear-gradient(135deg, $aqua 0%, $seaform 100%);
      color: $text-white;
      border-color: $aqua;
      font-weight: 900;
      box-shadow: 0 8px 24px rgba(0, 151, 215, 0.3);

      &:hover:not(:disabled) {
        background: linear-gradient(
          135deg,
          darken($aqua, 10%) 0%,
          darken($seaform, 10%) 100%
        );
        border-color: darken($aqua, 10%);
        box-shadow: 0 12px 36px rgba(0, 151, 215, 0.5);
        transform: translateY(-2px);
      }
    }
  }

  .cards-title-bar {
    display: flex;
    flex-direction: column;
    gap: 4px;
    position: relative;
    z-index: 1;
  }

  .cards-title {
    color: $navy;
    font-size: 20px;
    font-weight: 900;
    text-transform: uppercase;
    margin: 0;
    letter-spacing: 0.5px;
    transition: all 0.3s ease;
  }

  .cards-subtitle {
    color: $text-90-black;
    font-size: 13px;
    margin: 0;
    font-weight: 600;
  }

  .cards-price {
    margin: 28px 0 -24px 0;
    position: relative;
    z-index: 1;
  }

  .cards-price-amount {
    color: $navy;
    font-size: 38px;
    font-weight: 900;
    margin: 0;
    transition: all 0.3s ease;
  }

  .cards-price-validity {
    color: $text-90-black;
    display: block;
    font-size: 12px;
    text-transform: uppercase;
    font-weight: 700;
    letter-spacing: 0.4px;
  }

  .cards-action {
    margin-top: 20px;
    position: relative;
    z-index: 1;
  }

  .cards-action-button {
    background: linear-gradient(135deg, $navy 0%, $aqua 100%);
    border: 2px solid transparent;
    border-radius: 10px;
    color: $text-white;
    cursor: pointer;
    display: block;
    font-size: 14px;
    font-weight: 900;
    padding: 14px 24px;
    width: 100%;
    height: 52px;
    text-transform: uppercase;
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    letter-spacing: 0.6px;
    box-shadow: 0 8px 20px rgba(58, 103, 177, 0.25);
    position: relative;

    &:hover:not(:disabled) {
      transform: translateY(-4px);
      box-shadow: 0 12px 40px rgba(58, 103, 177, 0.4);
    }

    &:disabled {
      background: linear-gradient(135deg, #cbd5e1 0%, #94a3b8 100%);
      opacity: 0.8;
      cursor: not-allowed;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
      position: relative;

      &::after {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: repeating-linear-gradient(
          45deg,
          transparent,
          transparent 8px,
          rgba(255, 255, 255, 0.1) 8px,
          rgba(255, 255, 255, 0.1) 16px
        );
        border-radius: 8px;
        animation: slide 2s linear infinite;
      }
    }
  }

  .cards-ribbon {
    position: absolute;
    overflow: hidden;
    top: 0;
    right: 0;
    width: 150px;
    height: 150px;
    z-index: 10;
  }

  .cards-ribbon span {
    position: absolute;
    z-index: 15;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 200px;
    padding: 12px 0;
    background: linear-gradient(135deg, $aqua 0%, #0080c0 100%);
    box-shadow: 0 8px 24px rgba(0, 151, 215, 0.6);
    color: $text-white;
    font-size: 13px;
    font-weight: 900;
    text-transform: uppercase;
    text-align: center;
    right: -70px;
    top: 22px;
    transform: rotate(45deg);
    letter-spacing: 0.7px;
    animation: pulse 2s ease-in-out infinite;
    border-top: 2px solid rgba(255, 255, 255, 0.3);
    border-bottom: 2px solid rgba(255, 255, 255, 0.3);
  }

  .cards-ribbon span::before {
    content: "";
    position: absolute;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent 48%,
      rgba(255, 255, 255, 0.2) 50%,
      transparent 52%
    );
  }

  // ============================================
  // PLANS SECTION
  // ============================================
  .plans-section-header {
    text-align: center;
    margin: 48px 0 32px 0;
    animation: slideInUp 0.7s ease-out;

    .plans-title {
      font-size: 28px;
      font-weight: 900;
      margin: 0 0 12px 0;
      text-transform: uppercase;
      letter-spacing: 0.6px;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 16px;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;

      .plans-icon {
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
      }
    }

    .plans-subtitle {
      font-size: 15px;
      color: rgba(0, 0, 0, 0.65);
      margin: 0;
      font-weight: 600;
      letter-spacing: 0.2px;
    }
  }

  // ============================================
  // PAGE CONTENT
  // ============================================
  .page-content {
    padding: 0;
  }
  // ============================================
  // PROFILE STATUS ALERT
  // ============================================
  .profile-status-alert {
    border-radius: 12px !important;
    background: linear-gradient(
      135deg,
      rgba(220, 242, 252, 0.8) 0%,
      rgba(179, 229, 252, 0.6) 100%
    ) !important;
    border-left: 5px solid $info-color !important;
    animation: slideDown 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
    box-shadow: 0 8px 24px rgba(33, 150, 243, 0.15) !important;
    padding: 16px 20px !important;
    border: none !important;
  }

  .profile-status-title {
    font-size: 14px;
    font-weight: 700;
    color: $info-color;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    margin: 0;
  }

  .missing-steps-title {
    font-size: 14px;
    font-weight: 700;
    color: #ff9800;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    margin: 0;
  }

  .missing-steps-list {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
  // ============================================
  // RESPONSIVE DESIGN
  // ============================================
  @media (max-width: 960px) {
    .header-title {
      font-size: 28px;
    }

    .header-subtitle {
      font-size: 14px;
    }

    .form-card {
      margin-bottom: 16px;
    }

    .cards {
      width: auto;
      margin: 12px 8px;
    }

    .cards.popular {
      margin-top: -20px;
    }
  }

  @media (max-width: 600px) {
    .header-card {
      margin-bottom: 16px;
    }

    .header-title {
      font-size: 24px;
    }

    .header-subtitle {
      font-size: 14px;
    }

    .action-btn {
      padding: 6px !important;
    }

    .form-card {
      margin-bottom: 16px;

      .section-header {
        padding: 16px;
      }

      .section-title {
        font-size: 16px;
      }
    }

    .table-wrapper {
      overflow-x: auto;
    }

    .cards {
      width: 100%;
      margin: 8px 0;
    }

    .cards.popular {
      margin-top: 0;
      width: 100%;
    }
  }
}
</style>